import React, { useEffect, useMemo, useState } from "react";
import { Github, ExternalLink } from "lucide-react";
import { projects, projectCategories } from "../data/mock";

const ProjectCard = ({ p, index }) => {
  return (
    <article
      className="group border-t hairline grid grid-cols-12 gap-6 py-10 md:py-14 reveal in"
      data-cursor-hover
    >
      <div className="col-span-12 md:col-span-1 font-mono text-xs text-muted-foreground">
        0{index + 1}
      </div>
      <div className="col-span-12 md:col-span-5">
        <div className="aspect-[4/3] w-full border hairline rounded-md grid place-items-center relative overflow-hidden bg-muted/40">
          <div className="absolute inset-0 grid-bg opacity-70" />
          <div className="relative text-center px-6">
            <div className="font-display text-6xl md:text-8xl tracking-tighter">
              {p.cover}
            </div>
            <div className="font-mono text-[10px] text-muted-foreground mt-4 uppercase tracking-widest">
              {p.category} · {p.year}
            </div>
          </div>
        </div>
      </div>
      <div className="col-span-12 md:col-span-6">
        <div className="flex items-baseline justify-between gap-4">
          <h3 className="font-display text-3xl md:text-5xl leading-none group-hover:translate-x-1 transition-transform">
            {p.title}
          </h3>
          <div className="font-mono text-[11px] text-muted-foreground uppercase">
            {p.year}
          </div>
        </div>
        <div className="font-mono text-[11px] text-muted-foreground uppercase tracking-widest mt-3">
          {p.client}
        </div>
        <p className="mt-5 text-muted-foreground leading-relaxed max-w-2xl">
          {p.summary}
        </p>
        <ul className="mt-5 space-y-2">
          {p.bullets.map((b, i) => (
            <li key={i} className="flex gap-3 text-sm text-muted-foreground">
              <span className="font-mono text-xs mt-1">—</span>
              <span className="leading-relaxed">{b}</span>
            </li>
          ))}
        </ul>
        <div className="flex flex-wrap gap-2 mt-6">
          {p.stack.map((s) => (
            <span
              key={s}
              className="font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 border hairline rounded-full"
            >
              {s}
            </span>
          ))}
        </div>
        <div className="flex items-center gap-4 mt-6">
          {p.links.live && (
            <a
              href={p.links.live}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-sm link-grow"
              data-cursor-hover
            >
              <ExternalLink size={14} /> Live
            </a>
          )}
          {p.links.repo && (
            <a
              href={p.links.repo}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-sm link-grow"
              data-cursor-hover
            >
              <Github size={14} /> Repository
            </a>
          )}
          {!p.links.live && !p.links.repo && (
            <span className="font-mono text-[11px] text-muted-foreground uppercase tracking-widest">
              Private · Enterprise
            </span>
          )}
        </div>
      </div>
    </article>
  );
};

const normalize = (str) => str.trim().toLowerCase();

const Projects = () => {
  const [filter, setFilter] = useState("All");

  const filtered = useMemo(() => {
    if (filter === "All") return projects;
    return projects.filter((p) => normalize(p.category) === normalize(filter));
  }, [filter]);

  const countFor = (cat) => {
    if (cat === "All") return projects.length;
    return projects.filter((p) => normalize(p.category) === normalize(cat)).length;
  };

  return (
    <div>
      <section className="border-b hairline">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-20 md:pt-32 pb-16 md:pb-20">
          <div className="font-mono text-[11px] text-muted-foreground uppercase tracking-widest mb-5">
            04 — Work
          </div>
          <h1 className="display-xl text-5xl md:text-8xl leading-[0.95]">
            Selected <span className="italic font-light">projects.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-muted-foreground text-lg leading-relaxed">
            Enterprise platforms, internal tools and personal experiments. A small,
            honest selection of what I've shipped.
          </p>

          {/* Filter buttons */}
          <div className="flex flex-wrap gap-2 mt-10">
            {projectCategories.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                className={`px-4 py-2 border hairline rounded-full text-sm transition-colors ${
                  normalize(filter) === normalize(c)
                    ? "bg-foreground text-background border-foreground"
                    : "hover:bg-muted"
                }`}
                data-cursor-hover
              >
                {c}
                <span className="font-mono text-[10px] ml-2 opacity-70">
                  {countFor(c)}
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-20">
        {filtered.length > 0 ? (
          filtered.map((p, i) => (
            <ProjectCard key={p.id} p={p} index={i} />
          ))
        ) : (
          <div className="py-24 text-center text-muted-foreground font-mono text-sm uppercase tracking-widest">
            No projects found for "{filter}"
          </div>
        )}
        <div className="border-t hairline" />
      </section>

      <section className="border-t hairline">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32 grid grid-cols-1 md:grid-cols-2 gap-10 items-end">
          <h3 className="display-xl text-4xl md:text-6xl">
            More in the <span className="italic font-light">pipeline.</span>
          </h3>
          <p className="text-muted-foreground leading-relaxed max-w-md md:justify-self-end">
            Not every project makes the public grid. If you're curious about
            enterprise work under NDA, the fastest route is a conversation.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Projects;